import React, { useState } from 'react';
import { useSystemData } from '../contexts/SystemContext';
import { apiClient } from '../services/api';
import { BarChart3, TrendingUp, RefreshCw, RotateCcw, Users, Activity } from 'lucide-react';

const Analytics: React.FC = () => {
  const { analytics, isLoading, refreshData, forceRefreshData } = useSystemData();
  const [isResetting, setIsResetting] = useState(false);

  const resetAnalytics = async () => {
    try {
      setIsResetting(true);
      await apiClient.resetAnalytics();
      await refreshData(); // Refresh context data
    } catch (error) {
      console.error('Error resetting analytics:', error);
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Usage statistics and performance metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">Data refreshes every 2 minutes</span>
          <button
            onClick={forceRefreshData}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Main Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">
                {analytics?.analytics.total_unique_users || 0}
              </p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Users Today</p>
              <p className="text-3xl font-bold text-gray-900">
                {analytics?.analytics.users_today || 0}
              </p>
            </div>
            <Activity className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Requests</p>
              <p className="text-3xl font-bold text-gray-900">
                {analytics?.analytics.total_requests || 0}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Requests Today</p>
              <p className="text-3xl font-bold text-gray-900">
                {analytics?.analytics.requests_today || 0}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Breakdown */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Metrics</h3>
          <div className="h-32 flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Requests per User</span>
              <span className="text-sm font-medium">
                {analytics && analytics.analytics.total_unique_users > 0 ? 
                  Math.round(analytics.analytics.total_requests / analytics.analytics.total_unique_users) 
                  : 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Sessions</span>
              <span className="text-sm font-medium">{analytics?.active_sessions_count || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Users Active Today</span>
              <span className="text-sm font-medium">
                {analytics && analytics.analytics.total_unique_users > 0 ? 
                  Math.round((analytics.analytics.users_today / analytics.analytics.total_unique_users) * 100) 
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* User Engagement Trends */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Engagement Trends</h3>
          <div className="h-80 flex flex-col">
            {/* Daily Stats Table */}
            <div className="flex-1 overflow-hidden">
              <div className="h-48 overflow-y-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requests
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg/User
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics && Object.entries(analytics.analytics.daily_stats || {})
                      .sort(([a], [b]) => b.localeCompare(a)) // Sort by date descending
                      .slice(0, 5) // Show last 5 days to prevent stretching
                      .map(([date, stats]) => (
                      <tr key={date}>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {new Date(date).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {stats.unique_users || 0}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {stats.total_requests || 0}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {stats.unique_users > 0 
                            ? Math.round((stats.total_requests || 0) / stats.unique_users) 
                            : 0}
                        </td>
                      </tr>
                    ))}
                    {!analytics || Object.keys(analytics.analytics.daily_stats || {}).length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-gray-500 text-sm">
                          No engagement data available yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </div>
            
            {/* Quick Insights */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 mt-4">
              <div className="text-center">
                <p className="text-sm text-gray-500">Most Active Day</p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {analytics && Object.entries(analytics.analytics.daily_stats || {})
                    .reduce((max, [date, stats]) => 
                      (stats.total_requests || 0) > (max.requests || 0) 
                        ? { date: new Date(date).toLocaleDateString(), requests: stats.total_requests }
                        : max, 
                      { date: 'N/A', requests: 0 }
                    ).date}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Growth Trend</p>
                <p className="text-sm font-semibold text-green-600">
                  {analytics && Object.keys(analytics.analytics.daily_stats || {}).length >= 2 
                    ? '+' + Math.round(
                        (Object.keys(analytics.analytics.daily_stats).length / 7) * 100
                      ) + '% weekly'
                    : 'New'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 